import { useState, type FormEvent } from "react";
import { getToolTrelica, searchTrelicaApps, updateTool } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { useAsync } from "@/lib/useAsync";
import { Badge } from "@/components/ui/Badge";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { TextField } from "@/components/ui/TextField";
import type { Tool, TrelicaApp } from "@/api/types";

// A tool's live record from Trelica (1Password SaaS Manager), the system of record for
// tool inventory — fetched when the tool is opened, never stored here (see
// backend/src/capability_registry/integrations/trelica.py). Unlinked tools get a
// catalog search to set their trelica_app_id.

const HEADING = "mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase";

function errorText(e: unknown): string {
  return e instanceof ApiError ? e.errors.join("; ") : String(e);
}

/** The Trelica part of a tool's detail pane. A linked tool shows its live Trelica card,
 * fetched once when the tool is opened (and again if its link changes) — the only place
 * the app reads a tool's Trelica record. An unlinked tool, or one being relinked, shows
 * the linker instead. If the fetch fails (Trelica unreachable, or the stored id no longer
 * matches an app), the error is shown with the stored id and a way to change the link.
 * `onLinked` lets the page reload the tool so its new link and logo show. */
export function TrelicaSection({ tool, onLinked }: { tool: Tool; onLinked: () => void }) {
  const [relinking, setRelinking] = useState(false);
  const state = useAsync(() => getToolTrelica(tool.id), [tool.id, tool.trelica_app_id]);

  if (!tool.trelica_app_id || relinking) {
    return (
      <section>
        <h4 className={HEADING}>Trelica</h4>
        <TrelicaLinker
          tool={tool}
          onLinked={() => {
            setRelinking(false);
            onLinked();
          }}
          onCancel={tool.trelica_app_id ? () => setRelinking(false) : undefined}
        />
      </section>
    );
  }

  return (
    <section>
      <h4 className={HEADING}>Trelica</h4>
      {state.status === "loading" && (
        <p className="flex items-center gap-2 text-sm text-text/60">
          <Spinner className="h-3.5 w-3.5" /> Loading from Trelica…
        </p>
      )}
      {state.status === "error" && (
        <div className="flex flex-col gap-2 text-sm">
          <p className="text-warn">{state.message}</p>
          <p className="text-text/60">
            Linked to <span className="font-mono text-xs">{tool.trelica_app_id}</span>.{" "}
            <button onClick={() => setRelinking(true)} className="cursor-pointer text-accent underline">
              Change link
            </button>
          </p>
        </div>
      )}
      {state.status === "ready" && state.data.app && (
        <TrelicaCard app={state.data.app} onChangeLink={() => setRelinking(true)} />
      )}
    </section>
  );
}

/** A read-only summary of the tool's Trelica record: logo, status, vendor and category,
 * ownership and risk facts (only those Trelica has filled in), and links out to the app's
 * Trelica page, its Okta tile, and the product. Everything else stays in Trelica. */
function TrelicaCard({ app, onChangeLink }: { app: TrelicaApp; onChangeLink: () => void }) {
  const facts: [string, string | null][] = [
    ["Department", [app.department, app.business_unit].filter(Boolean).join(" · ") || null],
    ["Risk tier", app.risk_tier],
    ["Risk level", app.risk_level],
    ["Data classification", app.data_classification],
  ];
  return (
    <div className="flex flex-col gap-3 rounded border border-line p-3">
      <div className="flex items-center gap-3">
        <Logo src={app.logo_url} name={app.name} className="h-10 w-10" fallback />
        <div className="min-w-0">
          <p className="font-medium">
            {app.name} {app.status && <Badge className="ml-1">{app.status}</Badge>}
          </p>
          <p className="truncate text-xs text-text/60">{[app.vendor, app.category].filter(Boolean).join(" · ")}</p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        {facts
          .filter(([, v]) => v)
          .map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-text/50">{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        {app.trelica_url && (
          <a href={app.trelica_url} target="_blank" rel="noreferrer" className="text-accent underline">
            View in Trelica
          </a>
        )}
        {app.sign_on_url && (
          <a href={app.sign_on_url} target="_blank" rel="noreferrer" className="text-accent underline">
            Open in Okta
          </a>
        )}
        {app.app_url && (
          <a href={app.app_url} target="_blank" rel="noreferrer" className="text-accent underline">
            Product site
          </a>
        )}
        <button onClick={onChangeLink} className="ml-auto cursor-pointer text-xs text-text/60 underline">
          Change link
        </button>
      </div>
      {app.last_modified && (
        <p className="text-xs text-text/50">Updated in Trelica {new Date(app.last_modified).toLocaleDateString()}</p>
      )}
    </div>
  );
}

/** Finds the tool in Trelica's catalog and saves the chosen app as its trelica_app_id.
 * The search box starts with the tool's name and searches only on submit — never per
 * keystroke, to stay inside Trelica's rate limits. Apps whose Trelica status isn't
 * `Managed` carry a warning badge. Linking sends the tool's
 * `updated_at` as base_updated_at, so an edit someone else made since it loaded is refused
 * (409) rather than overwritten. `onCancel` is offered only when relinking an already
 * linked tool. */
function TrelicaLinker({ tool, onLinked, onCancel }: { tool: Tool; onLinked: () => void; onCancel?: () => void }) {
  const [query, setQuery] = useState(tool.name);
  const [results, setResults] = useState<TrelicaApp[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Runs one Trelica search for the current query and shows the matches. */
  async function search(e: FormEvent) {
    e.preventDefault(); // searches on submit only — never per keystroke
    setError(null);
    setBusy(true);
    try {
      setResults((await searchTrelicaApps(query.trim())).apps);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  /** Saves `app` as the tool's Trelica link; on success the parent reloads the tool. */
  async function link(app: TrelicaApp) {
    setError(null);
    setBusy(true);
    try {
      await updateTool(tool.id, { trelica_app_id: app.id }, tool.updated_at);
      onLinked();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 text-sm">
      {!onCancel && <p className="text-text/60">Not yet linked. Find this tool in Trelica's catalog:</p>}
      <form onSubmit={search} className="flex items-end gap-2">
        <TextField aria-label="Search Trelica" value={query} onChange={setQuery} className="flex-1" />
        <Button type="submit" isDisabled={busy || query.trim().length < 2}>
          Search
        </Button>
        {onCancel && (
          <Button onClick={onCancel} isDisabled={busy}>
            Cancel
          </Button>
        )}
      </form>
      {error && <p className="text-warn">{error}</p>}
      {results && results.length === 0 && <p className="text-text/60">No Trelica apps match “{query}”.</p>}
      {results && results.length > 0 && (
        <ul className="flex flex-col divide-y divide-line rounded border border-line">
          {results.map((app) => (
            <li key={app.id} className="flex items-center gap-3 p-2">
              <Logo src={app.logo_url} name={app.name} className="h-7 w-7" fallback />
              <div className="min-w-0 flex-1">
                <p className="truncate">
                  {app.name}
                  {app.status && app.status !== "Managed" && (
                    <Badge tone="warn" className="ml-2">
                      {app.status}
                    </Badge>
                  )}
                </p>
                <p className="truncate text-xs text-text/50">
                  {[app.vendor, app.category].filter(Boolean).join(" · ")}
                </p>
              </div>
              {app.trelica_url && (
                <a
                  href={app.trelica_url}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-xs text-accent underline"
                >
                  View in Trelica
                </a>
              )}
              <Button onClick={() => link(app)} isDisabled={busy || app.id === tool.trelica_app_id}>
                {app.id === tool.trelica_app_id ? "Linked" : "Link"}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
